import { useState } from "react";
import { Drawer, Input } from "antd";
import { SendOutlined } from "@ant-design/icons";

type Comment = {
  id: string;
  author: string;
  role: string;
  avatar_color: string;
  timestamp: string;
  text: string;
};

type CommentsData = Record<string, Comment[]>;
const INITIAL_COMMENTS: CommentsData = {};

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function initials(name: string): string {
  return name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export type MasteryCommentContext = {
  /** e.g. "site:NCT00261443" or "inv:NCT01080300" */
  recordKey: string;
  nctId: string;
  nameAsRegistered: string;
  source: "vivo_internal" | "ctgov";
  /** Extra label/value pairs shown below the header */
  contextLines?: { label: string; value: string }[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  context: MasteryCommentContext | null;
};

export default function MasteryCommentDrawer({
  open,
  onClose,
  context,
}: Props) {
  // Per-record comment state — merges initial data with any added during the session
  const [sessionComments, setSessionComments] = useState<
    Record<string, Comment[]>
  >({});
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!context) return null;

  const { recordKey, nctId, nameAsRegistered, source, contextLines } = context;

  const initial = INITIAL_COMMENTS[recordKey] ?? [];
  const added = sessionComments[recordKey] ?? [];
  const allComments = [...initial, ...added];

  const handleSubmit = () => {
    const text = draft.trim();
    if (!text) return;
    setSubmitting(true);
    const newComment: Comment = {
      id: `session-${Date.now()}`,
      author: "You",
      role: "Current User",
      avatar_color: "#2B4752",
      timestamp: new Date().toISOString(),
      text,
    };
    setSessionComments((prev) => ({
      ...prev,
      [recordKey]: [...(prev[recordKey] ?? []), newComment],
    }));
    setDraft("");
    setSubmitting(false);
  };

  return (
    <Drawer
      title={
        <div className="flex flex-col gap-0.5">
          <span className="text-[13px] font-semibold text-[#1F2933]">
            Record Annotations
          </span>
          <span className="text-[11px] font-normal text-[#8C8C8C]">
            {nctId}
          </span>
        </div>
      }
      placement="right"
      width={440}
      open={open}
      onClose={onClose}
      styles={{
        body: {
          padding: 0,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        },
      }}
    >
      {/* Record context header */}
      <div className="px-4 py-3 border-b border-[#F0F2F4] bg-[#F8FAFC] flex-shrink-0">
        <div className="flex items-center gap-2 mb-2">
          {source === "vivo_internal" ? (
            <span className="text-[10px] font-semibold text-[#22C55E] bg-[#F0FDF4] border border-[#BBF7D0] rounded px-1.5 py-0.5">
              Vivo
            </span>
          ) : (
            <span className="text-[10px] font-semibold text-[#6B8FA8] bg-[#EEF4F8] rounded px-1.5 py-0.5">
              ClinicalTrials.gov
            </span>
          )}
          <span className="text-[11px] font-mono text-[#2B4752]">{nctId}</span>
        </div>
        <div className="text-[13px] font-medium text-[#1F2933] leading-snug mb-2">
          {nameAsRegistered}
        </div>
        {contextLines && contextLines.length > 0 && (
          <div className="flex flex-col gap-0.5">
            {contextLines.map(({ label, value }) => (
              <div key={label} className="flex gap-2 text-[12px]">
                <span className="text-[#9CA3AF] w-[90px] shrink-0">
                  {label}
                </span>
                <span className="text-[#4B5563]">{value}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Comment thread */}
      <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-3">
        {allComments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="text-[13px] font-medium text-[#4B5563]">
              No annotations yet
            </div>
            <div className="text-[12px] text-[#9CA3AF] mt-1">
              Be the first to add a note about this record.
            </div>
          </div>
        ) : (
          allComments.map((c) => (
            <div key={c.id} className="flex gap-2.5">
              {/* Avatar */}
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0 mt-0.5"
                style={{ background: c.avatar_color }}
              >
                {initials(c.author)}
              </div>
              {/* Bubble */}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-1.5 mb-1">
                  <span className="text-[13px] font-semibold text-[#1F2933]">
                    {c.author}
                  </span>
                  <span className="text-[12px] text-[#9CA3AF]">{c.role}</span>
                  <span className="text-[11px] text-[#C4C9CE] ml-auto shrink-0">
                    {formatTimestamp(c.timestamp)}
                  </span>
                </div>
                <div className="text-[14px] text-[#374151] leading-relaxed bg-white border border-[#F0F2F4] rounded-[6px] px-3 py-2">
                  {c.text}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Comment input */}
      <div className="px-4 py-3 border-t border-[#F0F2F4] bg-white flex-shrink-0">
        <div className="flex gap-2 items-end">
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0"
            style={{ background: "#2B4752" }}
          >
            MB
          </div>
          <div className="flex-1 relative">
            <Input.TextArea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Add an annotation…"
              autoSize={{ minRows: 2, maxRows: 5 }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey))
                  handleSubmit();
              }}
              style={{ fontSize: 14, paddingRight: 36 }}
            />
            <button
              type="button"
              disabled={!draft.trim() || submitting}
              onClick={handleSubmit}
              className="absolute bottom-2 right-2 text-[#2B4752] disabled:text-[#C4C9CE] hover:text-[#0C98D0] transition-colors"
            >
              <SendOutlined style={{ fontSize: 14 }} />
            </button>
          </div>
        </div>
        <div className="text-[10px] text-[#C4C9CE] mt-1.5 text-right">
          ⌘ + Enter to submit
        </div>
      </div>
    </Drawer>
  );
}
